/**
 * Method step of the cycle configure wizard: a guide, not a setting.
 *
 * The method a session runs is the session's own choice; a cycle holds none. What
 * this step does is pick the criteria for the next step: choosing, say, RICE ticks
 * Reach, Impact, Confidence and Effort there. The choice is kept only in the
 * wizard's draft (`cycle_config_sessions`), so it is there when the person comes back.
 */

import { useState, useMemo } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Calculator,
  ListOrdered,
  Grid3X3,
  Users,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { TOOL_REGISTRY, type ToolDefinition } from '../../../lib/toolIntegration';
import { isToolSwitchedOff } from '../../../lib/switchedOffTools';
import { useCycleConfigStore } from '../../../stores/prioritization/cycleConfigStore';
import { getDecision, type ToolsDecisionValue } from '../../../types/prioritization/cycleConfigSession';

// ============================================================================
// Types
// ============================================================================

interface MethodSelectionPanelProps {
  sessionId: string;
  userId: string;
  onMethodSelected?: (toolId: string) => void;
}

interface CategoryConfig {
  id: ToolDefinition['category'];
  name: string;
  description: string;
  icon: typeof Calculator;
}

// ============================================================================
// Constants
// ============================================================================

const CATEGORIES: CategoryConfig[] = [
  {
    id: 'scoring',
    name: 'Scoring Methods',
    description: 'Calculate scores based on criteria values',
    icon: Calculator,
  },
  {
    id: 'ranking',
    name: 'Ranking Methods',
    description: 'Order items through comparison or ordering',
    icon: ListOrdered,
  },
  {
    id: 'framework',
    name: 'Framework Methods',
    description: 'Categorize items into predefined buckets or quadrants',
    icon: Grid3X3,
  },
  {
    id: 'collaborative',
    name: 'Collaborative Methods',
    description: 'Gather input from multiple participants',
    icon: Users,
  },
];

// ============================================================================
// Component
// ============================================================================

export function MethodSelectionPanel({
  sessionId,
  userId,
  onMethodSelected,
}: MethodSelectionPanelProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['scoring', 'ranking'])
  );
  
  // Store access
  const session = useCycleConfigStore(state => state.getConfigSession(sessionId));
  const proposeTools = useCycleConfigStore(state => state.proposeTools);
  
  // Get current selection
  const selectedToolId = useMemo(() => {
    const toolsValue = session ? (getDecision(session, 'tools')?.value as ToolsDecisionValue | undefined) : undefined;
    return toolsValue?.primaryToolId || toolsValue?.selectedToolIds?.[0] || null;
  }, [session]);
  
  // Group tools by category
  const toolsByCategory = useMemo(() => {
    const grouped: Record<string, ToolDefinition[]> = {};
    for (const tool of TOOL_REGISTRY) {
      if (isToolSwitchedOff(tool.id)) continue;
      if (!grouped[tool.category]) {
        grouped[tool.category] = [];
      }
      grouped[tool.category].push(tool);
    }
    return grouped;
  }, []);
  
  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };
  
  // Select a tool
  const handleSelectTool = (tool: ToolDefinition) => {
    proposeTools(sessionId, [tool.id], tool.id, userId);
    onMethodSelected?.(tool.id);
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)]">
          Method
        </h3>
        <p className="mt-1 text-sm text-[var(--text-sec)]">
          Optional. Pick the method you expect to score with, and the next step ticks the
          criteria it scores on. This does not set the method of any session: each session
          chooses its own when it is set up.
        </p>
      </div>
      
      {/* Current Selection */}
      {selectedToolId && (
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-[var(--green)]" />
            <span className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
              Selected: {TOOL_REGISTRY.find(t => t.id === selectedToolId)?.name}
            </span>
          </div>
        </div>
      )}
      
      {/* Category List */}
      <div className="space-y-4">
        {CATEGORIES.map(category => {
          const tools = toolsByCategory[category.id] || [];
          const isExpanded = expandedCategories.has(category.id);
          const CategoryIcon = category.icon;
          const hasSelectedTool = tools.some(t => t.id === selectedToolId);
          
          return (
            <div key={category.id} className="border border-[var(--rule)] overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className={`w-full flex items-center justify-between p-4 text-left hover:bg-[var(--surface)] transition-colors ${
                  hasSelectedTool ? 'bg-[var(--green-dim)]' : 'bg-[var(--bg-raised)]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <CategoryIcon className={`h-5 w-5 ${hasSelectedTool ? 'text-[var(--green)]' : 'text-[var(--text-ter)]'}`} />
                  <div>
                    <h4 className="font-medium text-[var(--text-pri)]">{category.name}</h4>
                    <p className="text-sm text-[var(--text-ter)]">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-ter)]">{tools.length} methods</span>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-[var(--text-ter)]" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-[var(--text-ter)]" />
                  )}
                </div>
              </button>
              
              {/* Tools List */}
              {isExpanded && (
                <div className="border-t border-[var(--rule)] bg-[var(--surface)] p-2">
                  <div className="grid gap-2">
                    {tools.map(tool => {
                      const isSelected = tool.id === selectedToolId;
                      
                      return (
                        <button
                          key={tool.id}
                          onClick={() => handleSelectTool(tool)}
                          className={`w-full text-left p-3  transition-colors ${
                            isSelected
                              ? 'bg-[var(--green-dim)] border-2 border-[var(--green)]'
                              : 'bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${isSelected ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                                  {tool.name}
                                </span>
                                {isSelected && <Check className="h-4 w-4 text-[var(--green)]" />}
                              </div>
                              <p className="text-sm text-[var(--text-sec)] mt-0.5">
                                {tool.description}
                              </p>
                              
                              {/* Required Attributes */}
                              {tool.requiresAttributes && tool.requiresAttributes.length > 0 && (
                                <div className="mt-2 flex items-center gap-2 text-xs">
                                  <AlertCircle className="h-3 w-3 text-[var(--mustard)]" />
                                  <span className="text-[var(--mustard)]">
                                    Requires: {tool.requiresAttributes.join(', ')}
                                  </span>
                                </div>
                              )}
                              
                              {/* Constraints */}
                              {(tool.minItems || tool.maxItems) && (
                                <div className="mt-1 text-xs text-[var(--text-ter)]">
                                  {tool.minItems && `Min ${tool.minItems} items`}
                                  {tool.minItems && tool.maxItems && ' • '}
                                  {tool.maxItems && `Max ${tool.maxItems} items`}
                                </div>
                              )}
                            </div>
                            
                            {/* Output badges */}
                            <div className="flex flex-col gap-1">
                              {tool.producesScore && (
                                <span className="px-2 py-0.5 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
                                  Scores
                                </span>
                              )}
                              {tool.producesRanking && (
                                <span className="px-2 py-0.5 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
                                  Ranking
                                </span>
                              )}
                              {tool.producesTiers && (
                                <span className="px-2 py-0.5 text-xs bg-[var(--mustard-dim)] text-[var(--mustard)] rounded">
                                  Tiers
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MethodSelectionPanel;
